import { useEffect, useRef } from "react";
import {
  useGetMailMessagesQuery,
  useSendMailMessageMutation,
} from "../../store/apiSlices/messages";
import { Message } from "../../types/Messages";
import { parseServerDate, timeAgo } from "../../utils";

export default function ChatMessages({ contactId }: { contactId: string }) {
  const containerRef = useRef<HTMLDivElement | null>(null);

  const {
    data: messages,
    isLoading,
    isError,
  } = useGetMailMessagesQuery(contactId, {
    skip: !contactId,
    pollingInterval: 60000,
  });

  //   console.log(messages);

  const [sendMailMessage] = useSendMailMessageMutation();

  const resendMessage = (msg: Message) => {
    sendMailMessage({
      vendor_contacts_id: contactId,
      message: msg.message,
      existingId: msg.id,
    });
  };

  const scrollToBottom = () => {
    const el = containerRef.current;
    if (!el) return;

    el.scrollTop = el.scrollHeight;
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  if (isLoading) return <ChatMessagesSkeleton />;

  if (isError)
    return (
      <div className="bg-gray-50 flex-1 p-4 overflow-y-auto space-y-3">
        <p className="text-gray-500 text-center text-sm">
          Something went wrong, please try again!
        </p>
      </div>
    );

  return (
    <div
      className="bg-gray-50 flex-1 p-4 overflow-y-auto space-y-4"
      ref={containerRef}
    >
      {messages && messages.length > 0 ? (
        messages.map((el) => (
          <div
            key={el.id}
            className={`flex items-end space-x-2 ${
              el.mail_from ? "justify-start" : "justify-end"
            }`}
          >
            {/* Avatar for received messages */}
            {el.mail_from && (
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center text-xs font-semibold text-gray-600">
                {el.mail_from.charAt(0).toUpperCase()}
              </div>
            )}

            {/* Message bubble + status */}
            <div className="flex flex-col max-w-[70%]">
              <div
                className={`px-3 py-2 rounded-lg shadow text-sm ${
                  el.mail_from
                    ? "bg-gray-200 text-gray-800 rounded-bl-none"
                    : "bg-blue-500 text-white rounded-br-none"
                }`}
              >
                {el.message}
              </div>

              {/* Status + Time */}
              <div
                className={`mt-1 flex items-center space-x-2 text-xs ${
                  el.mail_from ? "justify-start" : "justify-end"
                }`}
              >
                {/* Outgoing status */}
                {!el.mail_from && (
                  <>
                    {el.status === "pending" && (
                      <span className="text-gray-400">⏳ Sending...</span>
                    )}
                    {el.status === "failed" && (
                      <>
                        <span className="text-red-500">❌ Failed</span>
                        <button
                          className="text-blue-500 underline"
                          onClick={() => resendMessage(el)}
                        >
                          Resend
                        </button>
                      </>
                    )}
                  </>
                )}

                {/* Time ago for all */}
                <span className="text-gray-400">
                  {el.created_at
                    ? timeAgo(parseServerDate(el.created_at))
                    : timeAgo(new Date().toISOString())}
                </span>
              </div>
            </div>
          </div>
        ))
      ) : (
        <p className="text-gray-500 text-center text-sm">
          Messages with this contact will appear here…
        </p>
      )}
    </div>
  );
}

function ChatMessagesSkeleton() {
  return (
    <div className="flex flex-1 flex-col gap-4 p-4">
      {/* Left bubble */}
      <div className="flex items-start gap-2">
        <div className="flex flex-col gap-2">
          <div className="w-32 h-4 rounded-md bg-gray-200 animate-pulse" />
          <div className="w-24 h-4 rounded-md bg-gray-200 animate-pulse" />
        </div>
      </div>

      {/* Right bubble */}
      <div className="flex justify-end items-start gap-2">
        <div className="flex flex-col gap-2 items-end">
          <div className="w-40 h-4 rounded-md bg-gray-200 animate-pulse" />
          <div className="w-20 h-4 rounded-md bg-gray-200 animate-pulse" />
        </div>
      </div>

      {/* Left bubble */}
      <div className="flex items-start gap-2">
        <div className="flex flex-col gap-2">
          <div className="w-36 h-4 rounded-md bg-gray-200 animate-pulse" />
          <div className="w-28 h-4 rounded-md bg-gray-200 animate-pulse" />
        </div>
      </div>
    </div>
  );
}
